import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  KatSpinner,
  KatDataTable,
  KatPagination,
  KatFlashbar,
} from '@amzn/katal-react';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import { AuthContext } from 'src/components/auth/AuthContext';
import '../SourceReviewTables.scss';
import {
  GetAuthorizedStringGroupReviewHistoryInput,
  useGetAuthorizedStringGroupReviewHistoryQuery,
} from 'src/generated/graphql';
import { map } from 'lodash';
import completedReviewsColumnConfig, {
  CompletedReviewsTableColumn,
} from './CompletedReviewsColumnConfig';
import emptyInbox from 'src/images/empty-inbox.png';
import {
  getPaginatedData,
  getDisplayRange,
  compareValues,
  formatCompletedReviewRow,
} from '../Utils';
import CompletedReviewsTableFilter from './CompletedReviewsTableFilter';
import { Token } from '@amzn/awsui-components-react/polaris/property-filter/interfaces';

type Props = {
  id: string;
};

/**
 * Completed Reviews Table
 *
 * Displays completed source review requests for authorized string groups.
 */
const CompletedReviewsTable: React.FC<Props> = (props: Props) => {
  const { t, i18n } = useTranslation();
  const { userId } = useContext(AuthContext);

  const params: GetAuthorizedStringGroupReviewHistoryInput = {
    userAlias: userId,
  };

  /* Gql Hooks*/
  // Get authorized review history for a reviewer
  const {
    data: reviewHistoryData,
    loading: reviewHistoryLoading,
    error: reviewHistoryError,
  } = useGetAuthorizedStringGroupReviewHistoryQuery({
    variables: { params },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  // States for pagination, sorting and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortState, setSortState] = useState<KatDataTable.SortState>();
  const [filters, setFilters] = useState<Token[]>([]);
  const tableRef = useRef<KatDataTable.Element>(null);

  const columns = useMemo(
    () => completedReviewsColumnConfig(t),
    // Using i18n.language instead of t to avoid infinite re-renders since t reference changes on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  // Transform data to display
  const rowData = useMemo(() => {
    return map(
      reviewHistoryData?.getAuthorizedStringGroupReviewHistory.stringGroups,
      (row) => formatCompletedReviewRow(row, i18n.language)
    );
  }, [reviewHistoryData, i18n.language]);

  // Set initial sort order to show most recently updated reviews first
  useEffect(() => {
    if (!tableRef.current || !columns.length || !rowData?.length) return;
    const updateTimestampColumnIndex = columns.findIndex(
      (col) =>
        col.property === CompletedReviewsTableColumn.REVIEWED_TIMESTAMP.property
    );
    if (updateTimestampColumnIndex !== -1) {
      tableRef.current.setSortDirection(updateTimestampColumnIndex, 'desc');
    }
  }, [columns, rowData]);

  // Filter data based on applied filters
  const filteredData = useMemo(() => {
    // Return all data if no filters applied
    if (!rowData || filters.length === 0) return rowData;

    return rowData.filter((item) =>
      filters.every((filter) => {
        if (!filter.propertyKey) return true;
        // Get item's property value for case-insensitive comparison
        const itemPropertyValue =
          (item as any)[filter.propertyKey]?.toString().toLowerCase() || '';
        // Return item if property value contains the filter value
        return itemPropertyValue.includes((filter.value || '').toLowerCase());
      })
    );
  }, [rowData, filters]);

  // Sort data based on selected column and direction
  const sortedData = useMemo(() => {
    if (!sortState?.column?.property || !filteredData) return filteredData;

    const property = sortState.column.property;
    const direction = sortState.direction as 'asc' | 'desc';

    // Find column config to get sort property for timestamps
    const columnConfig = Object.values(CompletedReviewsTableColumn).find(
      (col) => col.property === property
    );
    const sortProperty = columnConfig?.sortProperty || property;
    const isTimestamp = columnConfig?.isTimestamp || false;

    return [...filteredData].sort((a, b) =>
      compareValues(a, b, sortProperty, direction, isTimestamp)
    );
  }, [filteredData, sortState]);

  const totalItems = sortedData?.length || 0;

  // Handle changes to number of items per page
  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newValue = parseInt(e.target.value, 10);
    setItemsPerPage(newValue);
    setCurrentPage(1);
  };

  // Handle page change
  const onPageChange = (e: KatPagination.ChangeEvent) => {
    const newPage = (e.target as any).page as number;
    setCurrentPage(newPage);
  };

  // Handle changes to sorting
  const onSortColumnChanged = useCallback(
    (e: KatDataTable.SortColumnChangedEvent) => {
      setSortState({
        column: e.detail.column,
        direction: e.detail.sortDirection,
      });
      setCurrentPage(1);
    },
    []
  );

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Token[]) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  // No-op filter function for loading, error and empty states
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noOpFilterChange = useCallback(() => {}, []);

  // Get data for current page based on pagination settings
  const currentPageData = useMemo(
    () => getPaginatedData(sortedData, currentPage, itemsPerPage),
    [sortedData, currentPage, itemsPerPage]
  );

  // Loading Page
  if (reviewHistoryLoading) {
    return (
      <>
        <CompletedReviewsTableFilter onFilterChange={noOpFilterChange} />
        <KatDataTable
          id={props.id + '-table'}
          columns={columns}
          className="lui-source-review-completed-reviews-table"
        ></KatDataTable>
        <KatSpinner id={props.id + '-spinner'} />
      </>
    );
  }

  // Error response page
  if (reviewHistoryError) {
    return (
      <>
        <CompletedReviewsTableFilter onFilterChange={noOpFilterChange} />
        <KatDataTable
          id={props.id + '-table'}
          columns={columns}
          className="lui-source-review-completed-reviews-table"
        ></KatDataTable>

        <div id={props.id + '-error-response'}>
          <div id={props.id + '-error-symbol'}>!</div>
          <div id={props.id + '-error-text'}>
            {t('source-review-error-response-string')}
          </div>
        </div>
      </>
    );
  }

  // Empty response page
  if (!rowData || rowData.length === 0) {
    return (
      <>
        <CompletedReviewsTableFilter onFilterChange={noOpFilterChange} />
        <KatDataTable
          id={props.id + '-table'}
          columns={columns}
          className="lui-source-review-completed-reviews-table"
        ></KatDataTable>
        <div id={props.id + '-empty-response'}>
          <img
            src={emptyInbox}
            alt="No Review Requests"
            id={props.id + '-empty-icon'}
          />
          <div id={props.id + '-history-empty-text'}>
            {t('source-review-history-empty-response-string')}
          </div>
        </div>
      </>
    );
  }

  // Happy case - show completed reviews with sorting and pagination
  return (
    <>
      <CompletedReviewsTableFilter onFilterChange={handleFilterChange} />
      <KatDataTable
        key="completed-reviews-table"
        id={props.id + '-table'}
        columns={columns}
        rowData={currentPageData}
        className="lui-source-review-completed-reviews-table"
        ref={tableRef}
        onSortColumnChanged={onSortColumnChanged}
      />
      {totalItems === 0 ? (
        /* Show flashbar when applied filters return no results */
        <KatFlashbar
          id={props.id + '-no-results-flashbar'}
          header={t('source-review-no-results-flashbar-header')}
          description={t('source-review-no-results-flashbar-description')}
          variant="info"
        />
      ) : (
        /* Show pagination controls when there are results */
        <div id={props.id + '-pagination-container'}>
          {/* Display range showing current items */}
          <div id={props.id + '-display-range'}>
            {t('source-review-requests-range-prefix')}{' '}
            {getDisplayRange(currentPage, itemsPerPage, totalItems)}
          </div>
          {/* Pagination component for navigating between pages */}
          <div id={props.id + '-pagination-wrapper'}>
            <KatPagination
              id={props.id + '-pagination'}
              page={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onChange={onPageChange}
            />
          </div>
          {/* Items per page selector */}
          <div id={`${props.id}-items-per-page`}>
            {t('source-review-results-per-page')}&nbsp;
            <select
              id={`${props.id}-items-per-page-select`}
              value={itemsPerPage.toString()}
              onChange={handleItemsPerPageChange}
            >
              <option value="10">10</option>
              <option value="20">20</option>
            </select>
          </div>
        </div>
      )}
    </>
  );
};

export default CompletedReviewsTable;
